import { CircleCircle } from '@calm/evexia-web-sdk';
import { useTracking } from '@rhg/libs/analytics';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    invalidateQueryKey,
    queryClient,
    queryKeys,
    useMutation,
} from '@rhg/libs/react-query';
import { getParsedCircleJwt } from '@rhg/utils/circle';
import { AxiosResponse } from 'axios';

export interface SponsorshipMutationRequest<TRequest> {
    partnerId: string;
    circle: CircleCircle;
    request: TRequest;
    willRefreshAllCircles?: boolean;
    activationToken?: string;
}

export function useSponsorshipMutation<TRequest>(
    callEndpoint: (
        api: ReturnType<typeof createCircleApi>,
        params: SponsorshipMutationRequest<TRequest>
    ) => Promise<AxiosResponse<CircleCircle>>
) {
    const { getAccessTokenSilently } = useAuth();
    const { setUserProfile, flush } = useTracking();

    return useMutation({
        mutationFn: async (params: SponsorshipMutationRequest<TRequest>) => {
            const api = createCircleApi(getAccessTokenSilently);
            const response = await callEndpoint(api, params);
            const parsedJwt = getParsedCircleJwt(
                response.data.authorizationJWT
            );
            await setUserProfile({
                PartnerID: params.partnerId,
                UserSponsoredCountry: parsedJwt?.countryCode?.toUpperCase(),
                IsDependent: response.data.sponsor?.isDependentGroup,
            });
            await flush();
            return response.data;
        },
        onSuccess: async (newCircle, { willRefreshAllCircles = true }) => {
            queryClient.setQueryData(
                [queryKeys.circleById, newCircle.recipient.id],
                newCircle
            );
            if (willRefreshAllCircles) {
                await invalidateQueryKey([queryKeys.circles]);
            }
        },
    });
}
